import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ZonePhotoThumbnail } from '../shared/ZonePhotoThumbnail';
import type { Zone, ZoneType } from '../../types';

const zoneTypeLabels: Record<ZoneType, string> = {
  expedition: 'Expedition',
  inventory: 'Inventory',
  production: 'Production',
  quarantine: 'Quarantine',
  calibration: 'Calibration',
  staging: 'Staging',
  returns: 'Returns',
};

interface ZoneCardProps {
  zone: Zone;
  locationCount?: number;
  activeCount?: number;
  onEdit?: () => void;
  onSelect?: () => void;
  isSelected?: boolean;
}

export function ZoneCard({
  zone,
  locationCount = 0,
  activeCount = 0,
  onEdit,
  onSelect,
  isSelected,
}: ZoneCardProps) {
  return (
    <Card
      className={`cursor-pointer transition-all hover:shadow-md ${
        isSelected ? 'ring-2 ring-primary' : ''
      }`}
      onClick={onSelect}
    >
      <CardHeader className="p-4 pb-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded-full shrink-0"
                style={{ backgroundColor: zone.color || '#6b7280' }}
              />
              <CardTitle className="text-base font-semibold">{zone.code}</CardTitle>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">{zone.name}</p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            <Badge variant="outline" className="text-xs">
              {zoneTypeLabels[zone.zone_type]}
            </Badge>
            <ZonePhotoThumbnail
              zone={zone}
              alt={`${zone.code} zone thumbnail`}
              className="h-14 w-20 rounded-md border bg-muted/20 shadow-sm"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {zone.description && (
          <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{zone.description}</p>
        )}
        <div className="flex items-center justify-between text-xs">
          <div className="flex gap-4">
            <span>
              <span className="font-medium">{locationCount}</span>{' '}
              <span className="text-muted-foreground">bins</span>
            </span>
            <span>
              <span className="font-medium">{activeCount}</span>{' '}
              <span className="text-muted-foreground">active</span>
            </span>
          </div>
          {onEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
            >
              Edit
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
